import { UserRole } from "@features/settings/types/settings.types";
import { useProfile } from "@shared/hooks/useProfile";
import theme from "@shared/utils/themeColors";
import React, { useEffect, useRef } from "react";
import { useSupport } from "../context/SupportContext";
import SupportChatPanel from "./SupportChatPanel";
import SupportTicketList from "./SupportTicketList";

/**
 * Admin dashboard for support chat
 * Only visible to users with ADMIN role
 */
const SupportAdminDashboard: React.FC = () => {
  const { user } = useProfile();
  const { selectedTicketId } = useSupport();

  // Only show for admin users
  if (!user || user.role !== UserRole.ADMIN) {
    return null;
  }

  // Also keep track of the tickets we've already visited in this session
  const visitedTicketsRef = useRef<Set<string>>(new Set());

  // When a ticket is selected, mark it as visited
  useEffect(() => {
    if (selectedTicketId) {
      visitedTicketsRef.current.add(selectedTicketId);
    }
  }, [selectedTicketId]);

  return (
    <div className={`h-full grid grid-cols-12 gap-0 ${theme.bg.secondary}`}>
      {/* Sidebar with ticket list */}
      <aside
        className={`col-span-3 border-r ${theme.border.primary} ${theme.bg.primary} overflow-y-auto`}
      >
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className={`text-lg font-semibold ${theme.text.primary}`}>
            Support Tickets
          </h2>
          <p className={`text-sm ${theme.text.secondary}`}>
            Manage user support requests
          </p>
        </div>
        <SupportTicketList />
      </aside>

      {/* Main chat area */}
      <main className="col-span-9 flex flex-col h-full">
        {selectedTicketId ? (
          <SupportChatPanel ticketId={selectedTicketId} />
        ) : (
          <div className="flex flex-col items-center justify-center h-full">
            <img
              src="/assets/images/avatar.png"
              alt="Support"
              className="w-20 h-20 rounded-full mb-4"
            />
            <h3 className={`text-xl font-semibold ${theme.text.primary} mb-2`}>
              Support Dashboard
            </h3>
            <p className={`text-center max-w-md ${theme.text.secondary}`}>
              Select a ticket from the list to start assisting users.
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default SupportAdminDashboard;
